import { useState, useRef, useEffect, useMemo } from 'react';
import type { Editor } from '@tiptap/core';
import { List, MessageSquare, Clock, Users, Paperclip, Trash2, Video, Image, FileText } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { uploadFiles, fetchFiles, deleteFile, downloadFile } from '@/store/fileSlice';
import { MAX_UPLOAD_SIZE_BYTES, MAX_UPLOAD_SIZE_MB } from '@/utils/constants';
import { HistorySection } from '../TaskDetails/HistorySection';
import { taskAPI } from '@/api/taskAPI';
import { useClipboardFilePaste } from '@/hooks/useClipboardFilePaste';
import AvatarTooltip from '@/components/AvatarTooltip';
import { FilePreviewModal } from '../FilePreviewModal';
import { FileCard } from '../FileCard';
import { RichTextEditor, isRichTextEmpty } from '@/components/RichTextEditor/RichTextEditor';
import { ensureHtml, sanitizeHtml, toPlainText, processHtmlForDisplay, processMentionsInHtml } from '@/utils/richText';
import { AssigneeChange, Comment, CommentActivity, User } from '@/types/comments';
import { FileMeta } from '@/types/files';
import { Task } from '@/types';
interface ActivitySectionProps {
  taskId: string;
  activity: CommentActivity[];
  comments: Comment[];
  onAddComment: (taskId: string, html: string, parentId?: string, plainText?: string) => Promise<Comment>;
  onDeleteComment: (commentId: string) => Promise<void>;
  users?: User[];
  onUpdateComment: (commentId: string, html: string, plainText?: string) => Promise<void>;
  task?: Task;
  isViewerOnly?: boolean;
}

export const ActivitySection: React.FC<ActivitySectionProps> = ({
  taskId,
  activity,
  comments,
  onAddComment,
  onDeleteComment,
  onUpdateComment,
  users,
  task,
  isViewerOnly,
}) => {
  const [activeTab, setActiveTab] = useState<'all' | 'comments' | 'history' | 'worklogs'>('all');
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(null);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null); // Track editing state
  const [editContent, setEditContent] = useState(''); // Content for editing
  const [updatingCommentId, setUpdatingCommentId] = useState<string | null>(null);
  const [assigneeChanges, setAssigneeChanges] = useState<AssigneeChange[]>([]);
  // User mention states
  const [showUserList, setShowUserList] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<{
    id: string;
    type: 'comment' | 'attachment';
  } | null>(null);
  const [previewFile, setPreviewFile] = useState<FileMeta | null>(null);
  // Derive filtered users from props and current query (depends on mentionQuery)
  const filteredUsers = useMemo(() => {
    const list = users || [];
    const q = (mentionQuery || '').toLowerCase();
    if (!q) return list;
    return list.filter(u => (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q));
  }, [users, mentionQuery]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [replyToId, setReplyToId] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [isReplying, setIsReplying] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([] as File[]);
  const [replyFiles, setReplyFiles] = useState<File[]>([] as File[]); // Files for reply
  const [commentUploadQueue, setCommentUploadQueue] = useState<Record<string, File[]>>({});
  const [uploadingCommentId, setUploadingCommentId] = useState<string | null>(null);
  const commentEditorRef = useRef<Editor | null>(null);
  const editCommentEditorRef = useRef<Editor | null>(null);
  const replyEditorRef = useRef<Editor | null>(null);
  type MentionTarget = 'comment' | 'edit' | 'reply';
  const [mentionContext, setMentionContext] = useState<{
    from: number;
    to: number;
    editor: Editor;
    target: MentionTarget;
    setContent: (html: string) => void;
  } | null>(null);
  const bytesToMegabytes = (bytes: number) => (bytes / (1024 * 1024)).toFixed(2);

  const partitionFilesBySizeLimit = (files: File[]) => {
    const accepted: File[] = [];
    const rejected: File[] = [];
    files.forEach((file) => {
      if (file.size > MAX_UPLOAD_SIZE_BYTES) {
        rejected.push(file);
      } else {
        accepted.push(file);
      }
    });
    return { accepted, rejected };
  };

  const notifyOversizedFiles = (files: File[], context?: string) => {
    if (!files.length) return;
    const first = files[0];
    const prefix = context ? `${context}: ` : '';
    let message = `${prefix}'${first.name}' is ${bytesToMegabytes(first.size)}MB. Maximum allowed size is ${MAX_UPLOAD_SIZE_MB}MB.`;
    if (files.length > 1) {
      message += ` ${files.length - 1} additional file(s) exceed the limit.`;
    }
    toast.error(message);
  };
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  // Combine comments and other activity items
  const activityIds = new Set(activity.map(a => a.id));
  const uniqueComments = comments.filter(c => !activityIds.has(c.id));
  const allActivity: (CommentActivity | Comment)[] = [
    ...activity,
    ...uniqueComments
  ].sort((a, b) => {
    const da = getDateValue(a) ?? 0;
    const db = getDateValue(b) ?? 0;
    return db - da;
  })
  useEffect(() => {
    loadAssigneeChanges();
  }, [taskId]);

 // In ActivitySection.tsx - Update the filteredActivity logic
const filteredActivity = activeTab === 'all'
  ? allActivity
  : activeTab === 'comments'
    ? comments.sort((a, b) =>
      new Date(getDateFromActivity(b)).getTime() - new Date(getDateFromActivity(a)).getTime()
    ): [];


  // Handle user mention functionality for any editor (comment/add, edit, reply)
  const handleEditorMention = (
    editor: Editor,
    target: MentionTarget,
    setContent: (html: string) => void
  ) => {
    // Track the active editor for the given context
    if (target === 'comment') commentEditorRef.current = editor;
    if (target === 'edit') editCommentEditorRef.current = editor;
    if (target === 'reply') replyEditorRef.current = editor;

    const { from } = editor.state.selection;
    const textBeforeCursor = editor.state.doc.textBetween(0, from, '\n', ' ');
    const match = /@([^\s@]*)$/i.exec(textBeforeCursor);

    if (match) {
      const query = match[1];
      setMentionQuery(query);
      setMentionContext({
        editor,
        from: from - query.length - 1,
        to: from,
        target,
        setContent
      });
      setShowUserList(true);
    } else {
      setShowUserList(false);
      setMentionQuery('');
      setMentionContext(null);
    }
  };

  // filteredUsers is derived via useMemo above; no extra state needed here

  const handleDeleteComment = (commentId: string) => {
    setPendingDeleteId(commentId);
    setIsDeleteModalOpen(true);
  };

  const confirmDeleteComment = async () => {
    if (!pendingDeleteId) return;
    setDeletingCommentId(pendingDeleteId);
    setIsDeleteModalOpen(false);
    try {
      await onDeleteComment(pendingDeleteId);
      const commentFiles = getCommentAttachments(pendingDeleteId);
      for (const file of commentFiles) {
        await dispatch(deleteFile(file._id));
      }
      toast.success('Comment deleted');
    } catch (error) {
      toast.error('Failed to delete comment');
    } finally {
      setDeletingCommentId(null);
      setPendingDeleteId(null);
    }
  };

 const loadAssigneeChanges = async () => {
    try {
      const changes = await taskAPI.getTaskAssigneeChanges(taskId);
      setAssigneeChanges(changes);
    } catch (error) {
      console.error('Failed to load assignee changes:', error);
      setAssigneeChanges([]);
    } finally {
    }
  };

  const handleUserSelect = (user: User) => {
    const context = mentionContext;
    const editor =
      context?.editor ||
      commentEditorRef.current ||
      editCommentEditorRef.current ||
      replyEditorRef.current;
    if (!editor) {
      setShowUserList(false);
      return;
    }

    const mentionLabel = `@${user.name || 'User'}`;
    const from = context ? context.from : editor.state.selection.from;
    const to = context ? context.to : editor.state.selection.to;

    editor
      .chain()
      .focus()
      .deleteRange({ from, to })
      .insertContent([
        {
          type: 'text',
          text: mentionLabel,
          marks: [
            {
              type: 'mention',
              attrs: {
                'data-mention-id': user.id,
                'data-mention-label': user.name || 'User'
              }
            }
          ]
        },
        {
          type: 'text',
          text: ' '
        }
      ])
      .run();

    const html = sanitizeHtml(editor.getHTML());
    if (context?.setContent) {
      context.setContent(html);
    } else {
      // Fallback to main comment state
      setComment(html);
    }
    setShowUserList(false);
    setMentionQuery('');
    setMentionContext(null);

    const newPos = from + mentionLabel.length + 1;
    setTimeout(() => {
      commentEditorRef.current?.commands.setTextSelection(newPos);
    }, 0);
  };

  const handleAddComment = async () => {
    const sanitizedComment = sanitizeHtml(comment);
    const commentIsEmpty = isRichTextEmpty(sanitizedComment);

    if (commentIsEmpty && selectedFiles.length === 0) return;

    if (commentIsEmpty && selectedFiles.length > 0) {
      toast.error('Comment cannot be empty');
      return;
    }

    const oversizedAttachments = selectedFiles.filter((file) => file.size > MAX_UPLOAD_SIZE_BYTES);
    if (oversizedAttachments.length) {
      notifyOversizedFiles(oversizedAttachments, 'Attachment');
      setSelectedFiles((prev) =>
        prev.filter((file) => file.size <= MAX_UPLOAD_SIZE_BYTES)
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const plainTextComment = toPlainText(sanitizedComment);
      const newComment = await onAddComment(taskId, sanitizedComment, replyToId || undefined, plainTextComment);

      // sanity: ensure we got an id from created comment
      const commentId = newComment?.id;

      // 2) If there are queued files, upload them with commentId
      if (selectedFiles.length > 0 && commentId) {
        // use unwrap() so errors surface as exceptions
        await dispatch(
          uploadFiles({
            files: selectedFiles,
            taskId,
            source: 'comment',
            commentId,
            uploadedBy: user?.id,
            uploadedUserEmail: user?.email,
          })
        ).unwrap();

        // refresh files for this comment (so attachments show under the comment)
        dispatch(fetchFiles({ taskId, source: 'comment', commentId }));
      }

      // 3) clear UI state
      setSelectedFiles([]);
      setReplyToId(null);
      setReplyContent('');
      setShowUserList(false);
      setMentionContext(null);
      setMentionQuery('');

      // Clear the editor content first, then clear state
      if (commentEditorRef.current) {
        commentEditorRef.current.commands.clearContent();
      }
      
      // Clear state after a brief delay to ensure editor clears first
      setTimeout(() => {
        setComment('');
      }, 0);

      toast.success('Comment added successfully');
    } catch (err) {
      console.error('handleAddComment error', err);
      toast.error('Failed to add comment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handleAddComment();
    }

    if (e.key === 'Escape' && showUserList) {
      setShowUserList(false);
    }
  };
  const historyCount = (task ? 1 : 0) + assigneeChanges.length + comments.length;
  const tabs = [
    { key: 'all' as const, label: 'All', icon: List, count: allActivity.length },
    { key: 'comments' as const, label: 'Comments', icon: MessageSquare, count: comments.length },
    { key: 'history' as const, label: 'History', icon: Clock, count: historyCount },
    { key: 'worklogs' as const, label: 'Worklogs', icon: Users, count: 0 }
  ];

  const handleEditComment = (commentId: string, currentContent: string) => {
    setEditingCommentId(commentId);
    // Use content_html if available, otherwise use content
    // ensureHtml will preserve existing links
    const content = (currentContent as any)?.content_html || currentContent || '';
    // Ensure mention markup is preserved for the editor (adds mention spans if missing)
    const normalized = ensureHtml(content);
    setEditContent(processMentionsInHtml(normalized));
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditContent('');
  };

  const handleSaveEdit = async (commentId: string) => {
    const sanitized = sanitizeHtml(editContent);
    if (isRichTextEmpty(sanitized)) {
      toast.error('Comment cannot be empty');
      return;
    }
    setUpdatingCommentId(commentId);

    try {
      const plainText = toPlainText(sanitized);
      await onUpdateComment(commentId, sanitized, plainText);
    } catch (error) {
      console.error('Failed to update comment:', error);
    }
    finally {
      setUpdatingCommentId(null);
      setEditingCommentId(null);
      setEditContent('');
    }
  };

  const uploadFilesForComment = async (commentId: string, filesToUpload: File[]) => {
    if (!filesToUpload || filesToUpload.length === 0) return;

    const oversized = filesToUpload.filter((file) => file.size > MAX_UPLOAD_SIZE_BYTES);
    let filesWithinLimit = filesToUpload.filter((file) => file.size <= MAX_UPLOAD_SIZE_BYTES);

    if (oversized.length) {
      notifyOversizedFiles(oversized, 'Attachment');
      setCommentUploadQueue((prev) => ({ ...prev, [commentId]: filesWithinLimit }));
      if (filesWithinLimit.length === 0) {
        return;
      }
    }

    try {
      setUploadingCommentId(commentId);
      await dispatch(
        uploadFiles({
          files: filesWithinLimit,
          taskId,
          source: 'comment',
          commentId,
          uploadedBy: user?.id,
          uploadedUserEmail: user?.email,
        })
      ).unwrap();

      setCommentUploadQueue((prev) => ({ ...prev, [commentId]: [] }));
      toast.success('Files uploaded');
    } catch (err) {
      console.error('uploadFilesForComment error', err);
      toast.error('Upload failed');
    } finally {
      setUploadingCommentId(null);
      dispatch(fetchFiles({ taskId, source: 'comment' }));
    }
  };

  const handleCommentFilesSelected = (commentId: string, files: FileList | File[]) => {
    const arr = Array.from(files || []);
    const { accepted, rejected } = partitionFilesBySizeLimit(arr);
    if (rejected.length) {
      notifyOversizedFiles(rejected, 'Attachment');
    }
    if (!accepted.length) return;
    setCommentUploadQueue((prev) => {
      const existing = prev[commentId] || [];
      return { ...prev, [commentId]: [...existing, ...accepted] };
    });
  };

  const handleCommentPaste = useClipboardFilePaste({
    contextLabel: 'comment-attachment',
    onFiles: (files) => {
      const { accepted, rejected } = partitionFilesBySizeLimit(files);
      if (rejected.length) {
        notifyOversizedFiles(rejected, 'Attachment');
      }
      if (!accepted.length) return;
      setSelectedFiles((prev) => [...prev, ...accepted]);
    },
  });

  const handleReplyPaste = useClipboardFilePaste({
    contextLabel: 'reply-attachment',
    onFiles: (files) => {
      const { accepted, rejected } = partitionFilesBySizeLimit(files);
      if (rejected.length) {
        notifyOversizedFiles(rejected, 'Reply attachment');
      }
      if (!accepted.length) return;
      setReplyFiles((prev) => [...prev, ...accepted]);
    },
  });

  const handleEditPaste = useClipboardFilePaste({
    contextLabel: 'comment-attachment',
    onFiles: (files) => {
      if (!editingCommentId) return;
      handleCommentFilesSelected(editingCommentId, files);
    },
  });



  useEffect(() => {
    if (taskId) {
      dispatch(fetchFiles({ taskId }));
    }
  }, [taskId, dispatch]);

  const getFileIcon = (type: string) => {
    if (type?.startsWith('image/')) {
      return <Image className="h-4 w-4 text-blue-500" />;
    } else if (type?.startsWith('video/')) {
      return <Video className="h-4 w-4 text-purple-500" />;
    } else {
      return <FileText className="h-4 w-4 text-gray-500" />;
    }
  };


  const { files } = useAppSelector((state) => state.files);

  const getCommentAttachments = (commentId: string): FileMeta[] => {
    return (files || []).filter(
      (f) => f.source === 'comment' && f.comment_id === commentId
    );
  };
  const renderCommentBody = (item: any) => {
    const html = ensureHtml((item as any)?.content_html || item?.content || '');
    if (isRichTextEmpty(html)) {
      return null;
    }
    return (
      <div
        className="text-gray-700 text-sm leading-relaxed prose prose-sm max-w-none break-words rich-text-view"
        dangerouslySetInnerHTML={{ __html: processHtmlForDisplay(html) }}
      />
    );
  };

  const buildCommentTree = (comments: any[]) => {
    // If backend already returns a nested tree (root comments with replies), just use it
    if (Array.isArray(comments) && comments.length > 0 && Array.isArray(comments[0]?.replies)) {
      return comments;
    }

    // Fallback: construct tree from a flat list
    const map: Record<string, any> = {};
    const roots: any[] = [];

    comments.forEach((c: any) => {
      map[c.id] = { ...c, replies: c.replies || [] };
    });

    comments.forEach((c: any) => {
      if (c.parent_comment_id && map[c.parent_comment_id]) {
        map[c.parent_comment_id].replies.push(map[c.id]);
      } else if (!c.parent_comment_id) {
        roots.push(map[c.id]);
      }
    });

    return roots;
  };


  const nestedComments = buildCommentTree(comments);

  const handleDeleteAttachment = async (fileId: string) => {
    setConfirmDelete({ id: fileId, type: 'attachment' });
  };


  const handleDownloadAttachment = async (fileId: string, fileName: string) => {
    try {
      await dispatch(downloadFile({ fileId, fileName })).unwrap();
    } catch {
      toast.error('Download failed');
    }
  };

  const renderComment = (item: any, level = 0) => (
    <div key={item.id} className={`flex gap-4 ${level > 0 ? 'ml-10 mt-4' : ''}`}>
      {/* Avatar */}
      <div className="flex-shrink-0">
        <AvatarTooltip label={item.user_name || item.user}>
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium bg-blue-500"
          >
            {getInitials(item.user_name || item.user)}
          </div>
        </AvatarTooltip>
      </div>

      {/* Comment Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 mb-1">
          <span className="font-semibold text-gray-900 text-sm">{item.user_name}</span>
          <span className="text-xs text-gray-500">•</span>
          <span className="text-xs text-gray-500">
            {formatRelativeTime(getDateFromActivity(item))} • {formatAbsoluteDate(getDateFromActivity(item))}
            {'updated_at' in item &&
              'created_at' in item &&
              item.updated_at !== item.created_at &&
              ' (edited)'}
          </span>
        </div>

        <div className="bg-white rounded-lg p-2">
          {editingCommentId === item.id ? (
            <div className="space-y-2">
              <div className="relative" onPaste={handleEditPaste}>
                <RichTextEditor
                  value={editContent}
                  onChange={(html) => setEditContent(html)}
                  onUpdate={({ editor }) => {
                    handleEditorMention(editor, 'edit', setEditContent);
                  }}
                  placeholder="Update your comment..."
                  onKeyDown={(event) => {
                    if (showUserList && mentionContext?.target === 'edit' && filteredUsers.length > 0 && event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault();
                      handleUserSelect(filteredUsers[0]);
                    }
                    if (event.key === 'Escape' && showUserList) {
                      setShowUserList(false);
                    }
                  }}
                  toolbarAccessory={
                    <label className="cursor-pointer text-gray-600 hover:text-gray-800 flex items-center gap-1">
                      <Paperclip className="w-4 h-4" />
                      <input
                        type="file"
                        multiple
                        hidden
                        onChange={(e) => {
                          if (!e.target.files) return;
                          handleCommentFilesSelected(item.id, e.target.files);
                          (e.target as HTMLInputElement).value = '';
                        }}
                      />
                    </label>
                  }
                />
                {/* User Mention Dropdown - Edit */}
                {showUserList && mentionContext?.target === 'edit' && filteredUsers.length > 0 && (
                  <div
                    className="absolute z-50 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto"
                    style={{ top: '100%', left: 0 }}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    {filteredUsers.map((user) => (
                      <button
                        key={user.id}
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleUserSelect(user);
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          handleUserSelect(user);
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 transition-colors"
                      >
                        <AvatarTooltip label={user.name || 'User'} position="bottom">
                          <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-medium">
                            {user.name ? user.name.substring(0, 2).toUpperCase() : 'U'}
                          </div>
                        </AvatarTooltip>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">{user.name || 'User'}</div>
                          {user.email && <div className="text-xs text-gray-500 truncate">{user.email}</div>}
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {/* Existing uploaded files - Card Format (Edit Mode - can delete) */}
                {getCommentAttachments(item.id).length > 0 && (
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                    {getCommentAttachments(item.id).map((file) => (
                      <FileCard
                        key={file._id}
                        file={file}
                        size="md"
                        showDelete={true}
                        onPreview={() => setPreviewFile(file)}
                        onDownload={() => handleDownloadAttachment(file._id, file.file_name)}
                        onDelete={() => handleDeleteAttachment(file._id)}
                      />
                    ))}
                  </div>
                )}

                {commentUploadQueue[item.id]?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {commentUploadQueue[item.id].map((f, i) => {
                      const currentDate = new Date().toLocaleDateString('en-GB'); // ✅ today's date

                      return (
                        <div
                          key={i}
                          className="flex items-center justify-between w-fit gap-3 px-2 py-1 bg-gray-50 border rounded text-xs"
                        >
                          <div className="flex items-center gap-2">
                            {getFileIcon(f.type)}
                            <span className="truncate max-w-[150px]" title={f.name}>
                              {f.name}
                            </span>
                          </div>

                          <div className="flex items-center gap-2 text-gray-500">
                            <button
                              onClick={() => {
                                setCommentUploadQueue((prev) => ({
                                  ...prev,
                                  [item.id]: prev[item.id].filter((_, index) => index !== i),
                                }));
                              }}
                              className="hover:text-red-600"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>

                            {/* ✅ Date moved to right side */}
                            <span className="text-[11px]">{currentDate}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* File upload */}
                <div className="absolute right-2 top-2 flex items-center gap-2">
                  {commentUploadQueue[item.id]?.length > 0 && (
                    <button
                      onClick={() => uploadFilesForComment(item.id, commentUploadQueue[item.id])}
                      className="text-xs px-2 py-1 bg-blue-600 text-white rounded"
                      disabled={uploadingCommentId === item.id}
                    >
                      {uploadingCommentId === item.id ? 'Uploading...' : 'Upload'}
                    </button>
                  )}
                </div>
              </div>

              {/* Save / Cancel */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleSaveEdit(item.id)}
                  disabled={updatingCommentId === item.id}
                  className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {updatingCommentId === item.id ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={handleCancelEdit}
                  className="px-3 py-1 text-sm text-gray-500 hover:text-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Comment text */}
              {renderCommentBody(item)}

              {/* Attachments - Card Format (View Mode - no delete) */}
              {getCommentAttachments(item.id).length > 0 && (
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {getCommentAttachments(item.id).map((file) => (
                    <FileCard
                      key={file._id}
                      file={file}
                      size="md"
                      showDelete={false}
                      onPreview={() => setPreviewFile(file)}
                      onDownload={() => handleDownloadAttachment(file._id, file.file_name)}
                      onDelete={() => handleDeleteAttachment(file._id)}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-4 mt-2">
          <button
            onClick={() => {
              setReplyToId(item.id);
              setReplyContent('');
              setReplyFiles([]); // Clear any previous reply files
            }}
            className="text-xs text-gray-500 hover:text-gray-700 hover:underline"
          >
            Reply
          </button>

          <button
            onClick={() => {
              // Pass content_html first to preserve links
              const content = (item as any).content_html || item.content || '';
              handleEditComment(item.id, content);
            }}
            className="text-xs text-gray-500 hover:text-gray-700 hover:underline"
          >
            Edit
          </button>

          <button
            onClick={() => {
              setPendingDeleteId(item.id);
              setIsDeleteModalOpen(true);
            }}
            disabled={deletingCommentId === item.id}
            className="text-xs text-gray-500 hover:text-red-600 hover:underline disabled:opacity-50"
          >
            {deletingCommentId === item.id ? 'Deleting...' : 'Delete'}
          </button>

        </div>

        {/* Replies */}
        {replyToId === item.id && (
          <div className="mt-3 ml-10">
            <div className="relative" onPaste={handleReplyPaste}>
              <RichTextEditor
                value={replyContent}
                onChange={(html) => setReplyContent(html)}
                onUpdate={({ editor }) => {
                  handleEditorMention(editor, 'reply', setReplyContent);
                }}
                placeholder={`Reply to ${item.user_name || item.user}...`}
                onKeyDown={(event) => {
                  if (showUserList && mentionContext?.target === 'reply' && filteredUsers.length > 0 && event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    handleUserSelect(filteredUsers[0]);
                  }
                  if (event.key === 'Escape' && showUserList) {
                    setShowUserList(false);
                  }
                }}
                toolbarAccessory={
                  <label className="cursor-pointer text-gray-600 hover:text-gray-800 flex items-center gap-1">
                    <Paperclip className="w-4 h-4" />
                    <input
                      type="file"
                      multiple
                      hidden
                      onChange={(e) => {
                        if (!e.target.files) return;
                        const files = Array.from(e.target.files);
                        const { accepted, rejected } = partitionFilesBySizeLimit(files);
                        if (rejected.length) {
                          notifyOversizedFiles(rejected, 'Reply attachment');
                        }
                        if (accepted.length) {
                          setReplyFiles((prev) => [...prev, ...accepted]);
                        }
                        (e.target as HTMLInputElement).value = '';
                      }}
                    />
                  </label>
                }
              />
              {/* User Mention Dropdown - Reply */}
              {showUserList && mentionContext?.target === 'reply' && filteredUsers.length > 0 && (
                <div
                  className="absolute z-50 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto"
                  style={{ top: '100%', left: 0 }}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  {filteredUsers.map((user) => (
                    <button
                      key={user.id}
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleUserSelect(user);
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        handleUserSelect(user);
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 transition-colors"
                    >
                      <AvatarTooltip label={user.name || 'User'} position="bottom">
                        <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-medium">
                          {user.name ? user.name.substring(0, 2).toUpperCase() : 'U'}
                        </div>
                      </AvatarTooltip>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">{user.name || 'User'}</div>
                        {user.email && <div className="text-xs text-gray-500 truncate">{user.email}</div>}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Display selected files for reply */}
            {replyFiles.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {replyFiles.map((f, i) => {
                  return (
                    <div
                      key={i}
                      className="flex items-center gap-2 px-2 py-1 bg-gray-50 border rounded text-xs"
                    >
                      {getFileIcon(f.type)}
                      <span className="truncate max-w-[150px]" title={f.name}>
                        {f.name}
                      </span>
                      <button
                        onClick={() =>
                          setReplyFiles((prev) => prev.filter((_, index) => index !== i))
                        }
                        className="text-gray-500 hover:text-red-600"
                        title="Remove"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="flex gap-2 mt-2">
              <button
                onClick={async () => {
                  const sanitizedReply = sanitizeHtml(replyContent);
                  const replyEmpty = isRichTextEmpty(sanitizedReply);
                  if (replyEmpty && replyFiles.length === 0) return;
                  
                  if (replyEmpty && replyFiles.length > 0) {
                    toast.error('Reply cannot be empty');
                    return;
                  }

                  const oversizedReplyFiles = replyFiles.filter((file) => file.size > MAX_UPLOAD_SIZE_BYTES);
                  if (oversizedReplyFiles.length) {
                    notifyOversizedFiles(oversizedReplyFiles, 'Reply attachment');
                    setReplyFiles((prev) =>
                      prev.filter((file) => file.size <= MAX_UPLOAD_SIZE_BYTES)
                    );
                    return;
                  }

                  setIsReplying(true);
                  try {
                    // 1) Create reply comment first
                    const replyPlainText = toPlainText(sanitizedReply);
                    const newReply = await onAddComment(taskId, sanitizedReply, item.id, replyPlainText);
                    
                    // 2) If there are files, upload them with the reply commentId
                    if (replyFiles.length > 0 && newReply?.id) {
                      await dispatch(
                        uploadFiles({
                          files: replyFiles,
                          taskId,
                          source: 'comment',
                          commentId: newReply.id,
                          uploadedBy: user?.id,
                          uploadedUserEmail: user?.email,
                        })
                      ).unwrap();

                      // Refresh files for this comment
                      dispatch(fetchFiles({ taskId, source: 'comment', commentId: newReply.id }));
                    }

                    // 3) Clear reply state
                    setReplyToId(null);
                    setReplyContent('');
                    setReplyFiles([]);
                    toast.success('Reply added successfully');
                  } catch (err) {
                    console.error('handleReply error', err);
                    toast.error('Failed to add reply');
                  } finally {
                    setIsReplying(false);
                  }
                }}
                disabled={(isRichTextEmpty(sanitizeHtml(replyContent)) && replyFiles.length === 0) || isReplying}
                className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {isReplying ? 'Replying...' : 'Reply'}
              </button>

              <button
                onClick={() => {
                  setReplyToId(null);
                  setReplyContent('');
                  setReplyFiles([]);
                }}
                className="px-3 py-1 text-sm text-gray-500 hover:text-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {item.replies && item.replies.length > 0 && (
          <div className="mt-2 space-y-2">
            {item.replies.map((reply: any) => renderComment(reply, level + 1))}
          </div>
        )}
      </div>
    </div>
  );


  return (
    <div className="mt-8">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Activity</h3>

      <div className="flex items-center gap-4 border-b border-gray-200 mb-6">
        {tabs.map(({ key, label, icon: Icon, count }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${activeTab === key
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
          >
            <Icon className="w-4 h-4" />
            {label}
            {count > 0 && (
              <span className="bg-gray-200 text-gray-700 text-xs px-2 py-1 rounded-full">
                {count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Comment Input */}
      {/* Add New Comment */}
      {(activeTab === 'all' || activeTab === 'comments') && (
        <div className="my-4 bg-gray-50 rounded-lg p-4 border border-gray-200">
          <h4 className="font-medium text-gray-900 mb-3">Add Comment</h4>

          <div className="relative">
            <div onPaste={handleCommentPaste}>
              <RichTextEditor
                value={comment}
                onChange={(html) => {
                  if (isViewerOnly) return;
                  setComment(html)
                }}
                onUpdate={({ editor }) => {
                  if (isViewerOnly) return;
                  handleEditorMention(editor, 'comment', setComment);
                }}
                placeholder="Add a comment... Type @ to mention someone (Ctrl+Enter to submit)"
                toolbarAccessory={
                  isViewerOnly ? null : (
                  <label className="cursor-pointer text-gray-600 hover:text-gray-800 flex items-center gap-1">
                    <Paperclip className="w-4 h-4" />
                    <input
                      type="file"
                      multiple
                      hidden
                      onChange={(e) => {
                        if (!e.target.files) return;
                        const files = Array.from(e.target.files);
                        const { accepted, rejected } = partitionFilesBySizeLimit(files);
                        if (rejected.length) {
                          notifyOversizedFiles(rejected, 'Attachment');
                        }
                        if (accepted.length) {
                          setSelectedFiles((prev) => [...prev, ...accepted]);
                        }
                        (e.target as HTMLInputElement).value = '';
                      }}
                    />
                  </label>
                  ) }
                onKeyDown={(event) => {
                  if (isViewerOnly) {
                    event.preventDefault();
                    return;
                  }
                  handleKeyPress(event);
                  if (showUserList && event.key === 'Enter' && !event.shiftKey && filteredUsers.length > 0) {
                    event.preventDefault();
                    handleUserSelect(filteredUsers[0]);
                  }
                  if (showUserList && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
                    event.preventDefault();
                  }
                  if (event.key === 'Escape' && showUserList) {
                    setShowUserList(false);
                  }
                }}
              />
            </div>

            {/* User Mention Dropdown */}
            {showUserList && mentionContext?.target === 'comment' && filteredUsers.length > 0 && (
              <div
                className="absolute z-50 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto"
                style={{ top: '100%', left: 0 }}
                onMouseDown={(e) => e.preventDefault()}
              >
                {filteredUsers.map((user) => (
                  <button
                    key={user.id}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleUserSelect(user);
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      handleUserSelect(user);
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 transition-colors"
                  >
                    <AvatarTooltip label={user.name || 'User'} position="bottom">
                      <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-medium">
                        {user.name ? user.name.substring(0, 2).toUpperCase() : 'U'}
                      </div>
                    </AvatarTooltip>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">{user.name || 'User'}</div>
                      {user.email && <div className="text-xs text-gray-500 truncate">{user.email}</div>}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* File queue */}
          {selectedFiles.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {selectedFiles.map((f, i) => {
                return (
                  <div
                    key={i}
                    className="flex items-center gap-2 px-2 py-1 bg-gray-50 border rounded text-xs"
                  >
                    {getFileIcon(f.type)}
                    <span className="truncate max-w-[150px]" title={f.name}>
                      {f.name}
                    </span>
                    <button
                      onClick={() =>
                        setSelectedFiles((prev) => prev.filter((_, index) => index !== i))
                      }
                      className="text-gray-500 hover:text-red-600"
                      title="Remove"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}


          {/* Action buttons */}
          <div className="flex justify-end gap-2 mt-4">
            <button
              onClick={() => {
                setComment('');
                setSelectedFiles([]);
                setShowUserList(false);
                setMentionContext(null);
                setMentionQuery('');
              }}
              disabled={isSubmitting}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleAddComment}
              disabled={(isRichTextEmpty(sanitizeHtml(comment)) && selectedFiles.length === 0) || isSubmitting}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {isSubmitting ? 'Adding...' : 'Comment'}
            </button>
          </div>
        </div>
      )}


      {/* Jira-style Comments Feed */}

{/* Content based on active tab */}
<div className="space-y-6">
  {activeTab === 'history' ? (
    <HistorySection taskId={taskId} comments={comments} task={task}/>
  ) : activeTab === 'comments' || activeTab === 'all' ? (
    <>
      {nestedComments.map((item) => renderComment(item))}
      
      {filteredActivity.length === 0 && (
        <div className="text-center py-8">
          <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-lg mb-2">
            {activeTab === "comments" ? "No comments yet" : "No activity found"}
          </p>
          {activeTab === "comments" && (
            <p className="text-gray-400 text-sm">Be the first to add a comment</p>
          )}
        </div>
      )}
    </>
  ) : (
    // For worklogs or other tabs
    <div className="text-center py-8">
      <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
      <p className="text-gray-500 text-lg mb-2">No worklogs found</p>
      <p className="text-gray-400 text-sm">Worklog functionality coming soon</p>
    </div>
  )}
</div>
      {isDeleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Delete Comment
            </h3>
            <p className="text-gray-600 text-sm mb-6">
              Are you sure you want to delete this comment? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setPendingDeleteId(null);
                }}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 rounded-lg hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteComment}
                disabled={deletingCommentId === pendingDeleteId}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deletingCommentId === pendingDeleteId ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Confirm Delete
            </h3>
            <p className="text-gray-600 text-sm mb-6">
              Are you sure you want to delete this {confirmDelete.type}? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 rounded-lg hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (confirmDelete.type === 'attachment') {
                    try {
                      await dispatch(deleteFile(confirmDelete.id)).unwrap();
                      toast.success('File deleted');
                    } catch {
                      toast.error('Failed to delete file');
                    }
                  }
                  setConfirmDelete(null);
                }}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* File Preview Modal */}
      <FilePreviewModal
        isOpen={!!previewFile}
        onClose={() => setPreviewFile(null)}
        file={previewFile}
      />

    </div>
  );
};

// --- DATE HELPERS ---

const getDateValue = (item: any): number | null => {
  if (!item) return null;

  // The item might itself be the date string already (like "October 30, 2025 at 10:11 AM")
  let raw =
    item.created_at ||
    item.updated_at ||
    item.date ||
    item.createdAt ||
    item.updatedAt ||
    item.timestamp ||
    item.time ||
    item; // <-- if item itself is a date string

  if (!raw) return null;

  // Handle Mongo-style { $date: "..." }
  if (typeof raw === "object" && raw.$date) {
    raw = raw.$date;
  }

  // Handle readable formats like "October 30, 2025 at 10:11 AM"
  if (typeof raw === "string" && raw.includes(" at ")) {
    raw = raw.replace(" at ", " ");
  }

  const parsed = Date.parse(raw);
  return isNaN(parsed) ? null : parsed;
};

const getDateFromActivity = (item: any): string => {
  const ms = getDateValue(item);
  if (!ms) return "";
  return new Date(ms).toISOString();
};



const formatAbsoluteDate = (isoDateString: string): string => {
  if (!isoDateString) return "";
  
  // Parse date - if no timezone info, assume UTC
  let date: Date;
  const tzRegex = /([zZ]|[+-]\d{2}:\d{2})$/;
  if (tzRegex.test(isoDateString)) {
    date = new Date(isoDateString);
  } else if (isoDateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
    // ISO format without timezone - assume UTC
    date = new Date(isoDateString + 'Z');
  } else {
    date = new Date(isoDateString);
  }
  
  if (isNaN(date.getTime())) return "";

  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

// Improved relative time formatting
const formatRelativeTime = (dateString: string): string => {
  if (!dateString) return "";
  
  // Parse date - if no timezone info, assume UTC
  let date: Date;
  const tzRegex = /([zZ]|[+-]\d{2}:\d{2})$/;
  if (tzRegex.test(dateString)) {
    date = new Date(dateString);
  } else if (dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
    // ISO format without timezone - assume UTC
    date = new Date(dateString + 'Z');
  } else {
    date = new Date(dateString);
  }
  
  if (isNaN(date.getTime())) return "";

  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInMinutes < 1) return "Just now";
  if (diffInMinutes < 60)
    return `${diffInMinutes} minute${diffInMinutes !== 1 ? "s" : ""} ago`;
  if (diffInHours < 24)
    return `${diffInHours} hour${diffInHours !== 1 ? "s" : ""} ago`;
  if (diffInDays < 7)
    return `${diffInDays} day${diffInDays !== 1 ? "s" : ""} ago`;

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};


const getInitials = (name: string): string => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};